import type { ApiRequest, PaginationLinks, PaginationMeta } from '@/types/api';
import type {
    AmountResource,
    SadadBill,
    SadadBiller,
    SadadBillerCategory,
    SadadBillPayment,
    SadadBillPaymentType,
    SadadBillServiceType,
    SadadBillStatus,
    SadadBillType,
} from '@/types/models';

export type SadadBillsRequest = ApiRequest<
    'GET',
    'sadad/bills',
    {
        data: SadadBill[];
    }
>;

export type SadadBillDetailsRequest = ApiRequest<
    'GET',
    `sadad/bills/${string}`,
    {
        data: SadadBill;
    }
>;

export type EditBillRequest = ApiRequest<
    'PUT',
    `sadad/bills/${string}`,
    { data: SadadBill },
    { name: string }
>;

export type SadadBillPaymentsRequest = ApiRequest<
    'GET',
    'sadad/payments',
    {
        data: SadadBillPayment[];
        links: PaginationLinks;
        meta: PaginationMeta;
    }
>;

export type SadadBillPaymentDetailsRequest = ApiRequest<
    'GET',
    `sadad/payments/${string}`,
    {
        data: SadadBillPayment;
    }
>;

export type ListSadadBillersRequest = ApiRequest<
    'GET',
    'sadad/billers',
    {
        data: SadadBiller[];
    }
>;

export type ListSadadBillersCategoriesRequest = ApiRequest<
    'GET',
    'sadad/billers/categories',
    {
        data: SadadBillerCategory[];
    }
>;
export type InquiredSadadBill = {
    biller_id: string;
    bill_number: string;
    billing_account: string;
    status: SadadBillStatus;
    service_type: SadadBillServiceType;
    due_amount: AmountResource;
    due_date: string;
    display_label: string;
    payment_range_list: [
        {
            type: 1; // BillCategory::Recurring
            range: {
                from: number;
                to: number;
            };
        },
    ];
};

export type InquireSadadBillRequest = ApiRequest<
    'POST',
    'sadad/bills/inquire',
    {
        data: InquiredSadadBill[];
    },
    {
        biller_id: string;
        bill_type: SadadBillType;
        billing_account: string;
    }
>;

export type PaySadadBillRequest = ApiRequest<
    'POST',
    'sadad/payments',
    {
        data: SadadBillPayment;
    },
    {
        biller_id: string;
        bill_number: string;
        source_bank_account_id: string;
        payment_method: 1;
        payment_type: SadadBillPaymentType;
        amount: string;
        currency: 'SAR';
        otp_token: string;
    }
>;

export type AddSadadBillToFavoritesRequest = ApiRequest<
    'POST',
    'sadad/bills',
    {
        data: SadadBill;
    },
    {
        biller_id: string;
        type: SadadBillType;
        number: string;
        name: string;
    }
>;

export type RefreshSadadBillRequest = ApiRequest<
    'POST',
    `sadad/bills/${string}/refresh`,
    {
        data: SadadBill;
    },
    never
>;
export type DeleteSadadBillRequest = ApiRequest<
    'DELETE',
    `sadad/bills/${string}`,
    void
>;

type SadadApi = {
    list: SadadBillsRequest['url'];
    details: (id: string) => SadadBillDetailsRequest['url'];
    edit: (id: string) => EditBillRequest['url'];
    delete: (id: string) => DeleteSadadBillRequest['url'];
    payments: SadadBillPaymentsRequest['url'];
    paymentDetails: (id: string) => SadadBillPaymentDetailsRequest['url'];
    listBillers: ListSadadBillersRequest['url'];
    listBillersCategories: ListSadadBillersCategoriesRequest['url'];
    inquireBill: InquireSadadBillRequest['url'];
    payBill: PaySadadBillRequest['url'];
    addBillToFavorites: AddSadadBillToFavoritesRequest['url'];
    refreshBill: (id: string) => RefreshSadadBillRequest['url'];
};

export const sadadApi: SadadApi = {
    list: 'sadad/bills',
    details: (id: string) => `sadad/bills/${id}`,
    edit: (id: string) => `sadad/bills/${id}`,
    delete: (id: string) => `sadad/bills/${id}`,
    payments: 'sadad/payments',
    paymentDetails: (id: string) => `sadad/payments/${id}`,
    listBillers: 'sadad/billers',
    listBillersCategories: 'sadad/billers/categories',
    inquireBill: 'sadad/bills/inquire',
    payBill: 'sadad/payments',
    addBillToFavorites: 'sadad/bills',
    refreshBill: (id: string) => `sadad/bills/${id}/refresh`,
};
